import { Injectable, inject } from '@angular/core';
import { create } from '@bufbuild/protobuf';
import {
  ProjectJobServiceCreateRequest,
  ProjectJobServiceCreateResponse,
  ProjectJobServiceCreateResponseSchema,
  ProjectJobServiceGetResponseSchema,
  ProjectJobServiceListRequest,
  ProjectJobServiceListRequestSchema,
  ProjectJobServiceUpdateRequest,
  ProjectJobServiceUpdateResponseSchema,
} from '@vmspdb/definitions';
import { ConnectProjectJob } from '@vmspdb/ui/connect';
import { ApiPaginated } from '@vmspdb/ui/domain';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { formatJobType } from '../standalone/pipes/job-type-display.pipe';
import { ApiRepositoryService } from './api-repository.service';
import { reencode } from './reencode';

@Injectable({
  providedIn: 'root',
})
export class ProjectJobRepositoryService implements ApiRepositoryService<
  ProjectJobServiceCreateRequest,
  ProjectJobServiceUpdateRequest,
  ProjectJobServiceListRequest,
  ProjectJobServiceCreateResponse
> {
  private api = inject(ConnectProjectJob);

  create(
    data: ProjectJobServiceCreateRequest,
  ): Observable<ProjectJobServiceCreateResponse> {
    return this.api.create(data).pipe();
  }

  delete(uuid: string): Observable<void> {
    return this.api.delete({ uuid }).pipe(
      map(() => {
        return;
      }),
    );
  }

  fetch(uuid: string): Observable<ProjectJobServiceCreateResponse> {
    return this.api
      .get({ uuid })
      .pipe(
        map((res) =>
          reencode(
            ProjectJobServiceGetResponseSchema,
            ProjectJobServiceCreateResponseSchema,
            res,
          ),
        ),
      );
  }

  list(
    request: ProjectJobServiceListRequest,
  ): Observable<ApiPaginated<ProjectJobServiceCreateResponse>> {
    return this.api.list(request);
  }

  update(
    data: ProjectJobServiceUpdateRequest,
  ): Observable<ProjectJobServiceCreateResponse> {
    return this.api
      .update(data)
      .pipe(
        map((res) =>
          reencode(
            ProjectJobServiceUpdateResponseSchema,
            ProjectJobServiceCreateResponseSchema,
            res,
          ),
        ),
      );
  }

  getOptions(projectUuid: string) {
    return this.list(
      create(ProjectJobServiceListRequestSchema, { projectUuid }),
    ).pipe(
      map((jobs) => jobs.items ?? []),
      map((jobs) =>
        jobs.map((job) => ({
          label: formatJobType(job.denormalized?.jobType),
          value: job.uuid,
        })),
      ),
    );
  }
}
